import {
    Action,
    userGet,
    userGetDone,
    userGetError,
    itemsGet,
    itemsGetDone,
    itemsGetFail,
    itemsPatch,
    itemsPatchDone,
    itemsPatchFail,
    syncMarkDirty,
    SyncMeta,
} from "../actions";
import { getDateTimeString } from "../utils/date";
import { User } from "../model/user";
import { isDayPrunable } from "../model/item";
import { Store } from "redux";
import { State } from "../state";
import type { PersistPartial } from "redux-persist/es/persistReducer";
import type { Persistor } from "redux-persist/es/types";
import { getUser as getUserFromState } from "../state/user.selectors";
import { getIsAuthorized } from "../state/user.selectors";
import {
    getLastSyncTime,
    getDirtyDates,
    getHasDirtyDates,
} from "../state/sync.selectors";
import { DirtyDates } from "../state/sync";
import { DaysJson, itemToEntityJson } from "./convert";

export const baseUrl = ``;

// matches mobile's DateTime(2020) "never synced" sentinel: the server
// rejects an unparseable `from`, so an empty lastSyncTime can't go verbatim
const SYNC_EPOCH = `2020-01-01T00:00:00.000Z`;
const SYNC_DEBOUNCE_MS = 3000;
const SYNC_INTERVAL_MS = 10 * 60 * 1000;
const SYNC_TIME_HEADER = `X-Sync-Time`;

export class UnauthorizedError extends Error {}

export const getUser = async (): Promise<User> => {
    const response = await window.fetch(`${baseUrl}/user`);
    if (response.status === 401) {
        throw new UnauthorizedError(`Unauthorized`);
    }
    if (!response.ok) {
        throw new Error(await response.text());
    }
    return response.json();
};

const postJson = async (url: string, body: unknown): Promise<Response> =>
    window.fetch(url, {
        method: `POST`,
        headers: { "Content-Type": `application/json` },
        body: JSON.stringify(body),
    });

const parseAuthResponse = async (response: Response): Promise<User> => {
    if (!response.ok) {
        throw new Error(await response.text());
    }
    const { user }: { user: User } = await response.json();
    return user;
};

export const logInWithEmail = async (
    email: string,
    password: string,
): Promise<User> =>
    parseAuthResponse(
        await postJson(`${baseUrl}/auth/login`, { email, password }),
    );

export const registerWithEmail = async (
    email: string,
    password: string,
): Promise<User> =>
    parseAuthResponse(
        await postJson(`${baseUrl}/auth/register`, { email, password }),
    );

export const logOut = async (): Promise<void> => {
    await window.fetch(`${baseUrl}/auth/logout`, { method: `POST` });
};

// Personal access tokens for 3rd-party tools (CLI/MCP/API) — the /api/v1
// token-management endpoints accept the same-origin session cookie, which
// is what lets Google-login users (who have no password) mint tokens here.

export type ApiTokenInfo = {
    id: string;
    name: string;
    scope: `read` | `readwrite`;
    createdAt: string;
    lastUsedAt: string | null;
};

export type ApiTokenCreated = ApiTokenInfo & { token: string };

const throwApiV1Error = async (response: Response): Promise<never> => {
    let message = `Request failed (${response.status})`;
    try {
        const body = await response.json();
        if (body && body.error && body.error.message) {
            message = body.error.message;
        }
    } catch {
        // not a JSON error envelope — keep the generic message
    }
    throw new Error(message);
};

export const listTokens = async (): Promise<Array<ApiTokenInfo>> => {
    const response = await window.fetch(`${baseUrl}/api/v1/tokens`);
    if (!response.ok) {
        return throwApiV1Error(response);
    }
    const { tokens }: { tokens: Array<ApiTokenInfo> } = await response.json();
    return tokens;
};

export const createToken = async (
    name: string,
    scope: `read` | `readwrite`,
): Promise<ApiTokenCreated> => {
    const response = await postJson(`${baseUrl}/api/v1/tokens`, {
        name,
        scope,
    });
    if (!response.ok) {
        return throwApiV1Error(response);
    }
    return response.json();
};

export const revokeToken = async (id: string): Promise<void> => {
    const response = await window.fetch(`${baseUrl}/api/v1/tokens/${id}`, {
        method: `DELETE`,
    });
    if (!response.ok) {
        return throwApiV1Error(response);
    }
};

export type WearAuthorizationInfo = {
    deviceId: string;
    deviceName: string;
    expiresAt: string;
};

const submitWearAuthorization = async (
    userCode: string,
    confirm: boolean,
): Promise<WearAuthorizationInfo> => {
    const response = await postJson(
        `${baseUrl}/api/v1/wear/authorizations/approve`,
        { userCode, confirm },
    );
    if (!response.ok) {
        return throwApiV1Error(response);
    }
    return response.json();
};

export const inspectWearAuthorization = async (
    userCode: string,
): Promise<WearAuthorizationInfo> => submitWearAuthorization(userCode, false);

export const approveWearAuthorization = async (
    userCode: string,
): Promise<WearAuthorizationInfo> => submitWearAuthorization(userCode, true);

let manualSyncTrigger: (() => void) | null = null;

export const triggerSync = (): void => {
    if (manualSyncTrigger) {
        manualSyncTrigger();
    }
};

// The sync engine. Mirrors mobile's ApiRepository (mobile/lib/repository.dart):
// GET when nothing is dirty, POST dirty days otherwise (deleted days as []
// tombstones), last-write-wins per day. Dirty marks live in the persisted
// data.sync slice and are only cleared by the DONE reducer when the pushed
// counter still matches, so a failed request needs no restore logic and
// mid-flight edits survive. Known limitation: two open tabs share one
// localStorage key and can clobber each other's persisted dirty marks;
// the server is safe either way (whole-day replace is idempotent).
export const initApi = (
    store: Store<State & PersistPartial, Action>,
    persistor: Persistor,
): (() => void) => {
    let isSyncing = false;
    let started = false;
    let wasAuthorized = false;
    let debounceTimer: number | undefined;
    let lastDirtyDates: DirtyDates | null = null;

    const syncNow = async (options?: {
        keepalive?: boolean;
    }): Promise<void> => {
        if (isSyncing) {
            // the in-flight request covers it; mid-flight edits keep their
            // dirty marks and trigger a follow-up sync below
            return;
        }
        if (!getIsAuthorized(store.getState())) {
            return;
        }
        // first sync after login: upload every local day worth keeping
        if (getLastSyncTime(store.getState()) === ``) {
            const days = store.getState().data.days;
            const localDates = Object.keys(days).filter(
                (date) => !isDayPrunable(days[date].present),
            );
            if (localDates.length > 0) {
                store.dispatch(syncMarkDirty(localDates));
            }
        }
        isSyncing = true;
        const state = store.getState();
        const syncTime = getDateTimeString(new Date());
        const from = getLastSyncTime(state) || SYNC_EPOCH;
        const pushedDates = { ...getDirtyDates(state) };
        const dates = Object.keys(pushedDates);
        const isPush = dates.length > 0;
        try {
            let response: Response;
            if (isPush) {
                const body = dates.reduce((result, date) => {
                    const dayData = state.data.days[date];
                    // a date that's dirty but absent from days was pruned
                    // locally -> push a deletion tombstone
                    result[date] = dayData
                        ? dayData.present.map((item) =>
                              itemToEntityJson(item, date),
                          )
                        : [];
                    return result;
                }, {} as DaysJson);
                store.dispatch(itemsPatch(dates));
                response = await window.fetch(
                    `${baseUrl}/items?changeDateTime=${encodeURIComponent(
                        syncTime,
                    )}&from=${encodeURIComponent(from)}`,
                    {
                        method: `POST`,
                        headers: { "Content-Type": `application/json` },
                        body: JSON.stringify(body),
                        keepalive: options && options.keepalive,
                    },
                );
            } else {
                store.dispatch(itemsGet());
                response = await window.fetch(
                    `${baseUrl}/items?from=${encodeURIComponent(from)}`,
                );
            }
            if (!getIsAuthorized(store.getState())) {
                // logged out while the request was in flight: drop the response
                return;
            }
            if (response.status === 401) {
                // session expired; dirty marks survive for after re-login
                store.dispatch(userGetError(`Unauthorized`));
                store.dispatch(
                    isPush
                        ? itemsPatchFail(`Unauthorized`)
                        : itemsGetFail(`Unauthorized`),
                );
                return;
            }
            if (!response.ok) {
                throw new Error(await response.text());
            }
            const payload: DaysJson = await response.json();
            const serverSyncTime = response.headers
                ? response.headers.get(SYNC_TIME_HEADER)
                : null;
            const meta: SyncMeta = {
                newSyncTime: serverSyncTime || syncTime,
                pushedDates,
            };
            store.dispatch(
                isPush
                    ? itemsPatchDone(payload, meta)
                    : itemsGetDone(payload, meta),
            );
        } catch (error) {
            // dirty marks were never cleared, so there is nothing to restore
            store.dispatch(
                isPush
                    ? itemsPatchFail(error.message)
                    : itemsGetFail(error.message),
            );
        } finally {
            isSyncing = false;
        }
        const stateAfter = store.getState();
        if (getHasDirtyDates(stateAfter) && getIsAuthorized(stateAfter)) {
            requestSync();
        }
    };

    const requestSync = (): void => {
        window.clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(() => {
            syncNow();
        }, SYNC_DEBOUNCE_MS);
    };

    const validateUserAndSync = async (): Promise<void> => {
        const persistedUser = getUserFromState(store.getState());
        const wasPersistedAuthorized = getIsAuthorized(store.getState());
        store.dispatch(userGet());
        try {
            const user = await getUser();
            store.dispatch(userGetDone(user));
        } catch (error) {
            if (error instanceof UnauthorizedError) {
                store.dispatch(userGetError(error.message));
                return;
            }
            if (wasPersistedAuthorized && persistedUser != null) {
                // transient network failure must not drop a persisted
                // session; restore the persisted user and retry via the
                // periodic/focus triggers (a real 401 deauths in syncNow)
                store.dispatch(userGetDone(persistedUser));
            } else {
                store.dispatch(userGetError(error.message));
                return;
            }
        }
        syncNow();
    };

    const onStoreChange = (): void => {
        const state = store.getState();
        if (!state._persist.rehydrated) {
            return;
        }
        if (!started) {
            started = true;
            wasAuthorized = getIsAuthorized(state);
            lastDirtyDates = getDirtyDates(state);
            validateUserAndSync();
            return;
        }
        const isAuthorized = getIsAuthorized(state);
        const justLoggedIn = isAuthorized && !wasAuthorized;
        wasAuthorized = isAuthorized;
        if (justLoggedIn) {
            syncNow();
            return;
        }
        const dirtyDates = getDirtyDates(state);
        if (
            isAuthorized &&
            dirtyDates !== lastDirtyDates &&
            Object.keys(dirtyDates).length > 0
        ) {
            requestSync();
        }
        lastDirtyDates = dirtyDates;
    };

    const onVisibilityChange = (): void => {
        if (document.visibilityState === `visible`) {
            syncNow();
        } else {
            flush();
        }
    };

    const onFocus = (): void => {
        syncNow();
    };

    const flush = (): void => {
        // the tab may be going away: make dirty marks durable (persist
        // throttle is 500ms) and push immediately, surviving unload via
        // fetch keepalive
        window.clearTimeout(debounceTimer);
        persistor.flush();
        const state = store.getState();
        if (getHasDirtyDates(state) && getIsAuthorized(state)) {
            syncNow({ keepalive: true });
        }
    };

    const unsubscribe = store.subscribe(onStoreChange);
    const intervalId = window.setInterval(() => {
        syncNow();
    }, SYNC_INTERVAL_MS);
    document.addEventListener(`visibilitychange`, onVisibilityChange);
    window.addEventListener(`focus`, onFocus);
    window.addEventListener(`pagehide`, flush);
    manualSyncTrigger = () => {
        syncNow();
    };
    // handle the case where rehydration finished before we subscribed
    onStoreChange();

    return () => {
        unsubscribe();
        window.clearTimeout(debounceTimer);
        window.clearInterval(intervalId);
        document.removeEventListener(`visibilitychange`, onVisibilityChange);
        window.removeEventListener(`focus`, onFocus);
        window.removeEventListener(`pagehide`, flush);
        manualSyncTrigger = null;
    };
};
